import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronDown, ChevronUp, Save, X } from 'lucide-react';

const selectClass =
  'mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#003366]';

const EnumSelect = ({ id, label, value, options, onChange, testId }) => (
  <div>
    <Label htmlFor={id} className="text-sm">{label}</Label>
    <select id={id} value={value || ''} onChange={(e) => onChange(e.target.value)}
      className={selectClass} data-testid={testId}>
      <option value="">Select…</option>
      {Object.entries(options || {}).map(([v, l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  </div>
);

const MultiChips = ({ label, options, values, onChange, testId }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1 flex flex-wrap gap-2" data-testid={testId}>
      {Object.entries(options || {}).map(([v, l]) => {
        const on = (values || []).includes(v);
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(on ? values.filter((x) => x !== v) : [...(values || []), v])}
            data-testid={`${testId}-${v}`}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              on ? 'bg-[#003366] text-white border-[#003366]'
                 : 'bg-white text-slate-600 border-slate-300 hover:border-[#003366]'
            }`}
          >
            {l}
          </button>
        );
      })}
    </div>
  </div>
);

export const FlowForm = ({ catalogue, initial, onSave, onCancel, busy, idPrefix = 'flow' }) => {
  const [form, setForm] = useState({
    system_name: '', system_type: '', role: '', direction: '',
    data_categories: [], data_principal_types: [],
    purpose: '', lawful_basis: 'unknown', retention_bucket: 'unknown',
    retention_months: '', hosting_region: 'unknown', hosting_country: '',
    dpa_in_place: 'unknown', recipient_name: '',
    ...(initial || {}),
  });
  const [showDetail, setShowDetail] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const ready = form.system_name && form.system_type && form.role && form.direction
    && (form.data_categories || []).length > 0 && (form.data_principal_types || []).length > 0;

  return (
    <div className="space-y-4" data-testid={`${idPrefix}-form`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`${idPrefix}-name`} className="text-sm">System or party name</Label>
          <Input id={`${idPrefix}-name`} value={form.system_name}
            onChange={(e) => set('system_name', e.target.value)}
            placeholder="e.g. Tally, Razorpay, our CA firm"
            className="mt-1" data-testid={`${idPrefix}-name-input`} />
        </div>
        <EnumSelect id={`${idPrefix}-type`} label="What kind of system is it?"
          value={form.system_type} options={catalogue.system_types}
          onChange={(v) => set('system_type', v)} testId={`${idPrefix}-type-select`} />
        <EnumSelect id={`${idPrefix}-role`} label="Who holds the data?"
          value={form.role} options={catalogue.roles}
          onChange={(v) => set('role', v)} testId={`${idPrefix}-role-select`} />
        <EnumSelect id={`${idPrefix}-direction`} label="Which way does the data move?"
          value={form.direction} options={catalogue.directions}
          onChange={(v) => set('direction', v)} testId={`${idPrefix}-direction-select`} />
      </div>

      <MultiChips label="What personal data is involved?" options={catalogue.data_categories}
        values={form.data_categories} onChange={(v) => set('data_categories', v)}
        testId={`${idPrefix}-categories`} />
      <MultiChips label="Whose data is it?" options={catalogue.data_principal_types}
        values={form.data_principal_types} onChange={(v) => set('data_principal_types', v)}
        testId={`${idPrefix}-principals`} />

      <button type="button" onClick={() => setShowDetail((s) => !s)}
        className="text-sm text-[#008080] font-[600] flex items-center gap-1"
        data-testid={`${idPrefix}-toggle-detail-btn`}>
        {showDetail ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        Add detail (optional — leave as “Not sure yet” and we will flag it as a gap)
      </button>

      {showDetail && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-200 pt-4"
          data-testid={`${idPrefix}-detail-block`}>
          <div className="md:col-span-2">
            <Label htmlFor={`${idPrefix}-purpose`} className="text-sm">Why is this data used?</Label>
            <Input id={`${idPrefix}-purpose`} value={form.purpose || ''}
              onChange={(e) => set('purpose', e.target.value)}
              placeholder="e.g. invoicing and GST filing" className="mt-1"
              data-testid={`${idPrefix}-purpose-input`} />
          </div>
          <EnumSelect id={`${idPrefix}-basis`} label="Lawful basis"
            value={form.lawful_basis} options={catalogue.lawful_bases}
            onChange={(v) => set('lawful_basis', v)} testId={`${idPrefix}-basis-select`} />
          <EnumSelect id={`${idPrefix}-retention`} label="How long is it kept?"
            value={form.retention_bucket} options={catalogue.retention_buckets}
            onChange={(v) => set('retention_bucket', v)} testId={`${idPrefix}-retention-select`} />
          <div>
            <Label htmlFor={`${idPrefix}-months`} className="text-sm">Exact months (optional)</Label>
            <Input id={`${idPrefix}-months`} type="number" min="0" value={form.retention_months ?? ''}
              onChange={(e) => set('retention_months', e.target.value)} className="mt-1"
              data-testid={`${idPrefix}-months-input`} />
          </div>
          <EnumSelect id={`${idPrefix}-hosting`} label="Where is it hosted?"
            value={form.hosting_region} options={catalogue.hosting_regions}
            onChange={(v) => set('hosting_region', v)} testId={`${idPrefix}-hosting-select`} />
          <EnumSelect id={`${idPrefix}-dpa`} label="Is there a signed agreement?"
            value={form.dpa_in_place} options={catalogue.dpa_states}
            onChange={(v) => set('dpa_in_place', v)} testId={`${idPrefix}-dpa-select`} />
          <div>
            <Label htmlFor={`${idPrefix}-recipient`} className="text-sm">Recipient / vendor name</Label>
            <Input id={`${idPrefix}-recipient`} value={form.recipient_name || ''}
              onChange={(e) => set('recipient_name', e.target.value)} className="mt-1"
              data-testid={`${idPrefix}-recipient-input`} />
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button onClick={() => onSave({
          ...form,
          retention_months: form.retention_months === '' ? null : Number(form.retention_months),
        })} disabled={busy || !ready}
          className="bg-[#003366] hover:bg-[#002244] rounded-sm" data-testid={`${idPrefix}-save-btn`}>
          <Save className="h-4 w-4 mr-2" /> Save flow
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={busy} data-testid={`${idPrefix}-cancel-btn`}>
            <X className="h-4 w-4 mr-2" /> Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

export default FlowForm;
